/**
 * The project set-up wizard's reads and writes in Teslyn Cloud (batch 6 F13, F26; docs/specs/cloud/project-setup-
 * wizard.md) and the report emails' settings (F07). RLS decides every write: tenant admins, managers and office staff
 * change the project's set-up rows (policies `project_write`, `block_write`, `position_write`), the report emails and
 * the wizard's steps; every staff role reads them. The checks are the domain's (`projectSetupWizard`).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  localIsoDate,
  type Aircraft,
  type Block,
  type ChecklistInput,
  type Person,
  type PlannedLine,
  type Position,
  type Project,
  type ProjectCrew,
  type ProjectSetupInput,
  type ReportEmailSetting,
  type SetupConfirmation,
} from '@teslyn/domain';
import { all, plainPortalError, type PortalProject, type Res } from '../portal/api.ts';

function must<T>(r: Res<T>): T {
  if (r.error) throw new Error(plainPortalError(r.error));
  return r.data as T;
}

export interface SetupData {
  readonly input: ProjectSetupInput;
  readonly project: Project;
  readonly blocks: readonly Block[];
  readonly positions: readonly Position[];
}

/** Everything the wizard's checks read, from the cloud's rows (the same shapes as Teslyn Field's). */
export async function loadSetup(
  sb: SupabaseClient,
  p: PortalProject,
  today: string = localIsoDate(),
): Promise<SetupData> {
  const [project, blocks, plannedLines, aircraft, people, crew, positions, trackers, units, links, steps] =
    await Promise.all([
      sb.from('project').select('*').eq('id', p.id).maybeSingle(),
      sb.from('block').select('*').eq('project_id', p.id),
      all<Pick<PlannedLine, 'block_id' | 'deleted_at'>>((from, to) =>
        sb.from('planned_line').select('block_id, deleted_at').eq('project_id', p.id).range(from, to),
      ),
      sb.from('aircraft').select('*').eq('tenant_id', p.tenantId),
      sb.from('person').select('*').eq('tenant_id', p.tenantId),
      sb.from('project_crew').select('*').eq('project_id', p.id),
      sb.from('position').select('*').eq('project_id', p.id),
      sb.from('tracker_asset').select('aircraft_id, deleted_at').eq('tenant_id', p.tenantId),
      sb.from('sensor_station').select('id').eq('tenant_id', p.tenantId).eq('active', true),
      sb.from('project_base_station').select('station_id, starts_on, ends_on').eq('project_id', p.id),
      sb.from('project_setup_step').select('step, confirmed_by, confirmed_at').eq('project_id', p.id),
    ]);
  const [setting, people2, names] = await Promise.all([
    // every column: a database before 20261030100000 has daily and weekly only (decisions 883, 884)
    sb.from('project_report_email').select('*').eq('project_id', p.id).maybeSingle(),
    p.clientOrgId
      ? sb.from('client_membership').select('user_id').eq('client_org_id', p.clientOrgId)
      : Promise.resolve({ data: null, error: null }),
    sb
      .from('membership')
      .select('user_id, app_user:user_id (email, display_name)')
      .eq('tenant_id', p.tenantId),
  ]);
  const nameOf = new Map<string, string>();
  for (const m of must(
    names as Res<
      {
        user_id: string;
        app_user: { email: string; display_name: string } | { email: string; display_name: string }[] | null;
      }[]
    >,
  ) ?? []) {
    const u = Array.isArray(m.app_user) ? m.app_user[0] : m.app_user;
    if (u) nameOf.set(m.user_id, u.display_name || u.email);
  }
  const confirmed: SetupConfirmation[] = (
    must(steps as Res<{ step: string; confirmed_by: string | null; confirmed_at: string }[]>) ?? []
  ).map((s) => ({
    step: s.step,
    confirmed_by_name: s.confirmed_by ? (nameOf.get(s.confirmed_by) ?? null) : null,
    confirmed_at: s.confirmed_at,
  }));
  const proj = must(project as Res<Project>);
  const blockRows = (must(blocks as Res<Block[]>) ?? []) as Block[];
  const positionRows = (must(positions as Res<Position[]>) ?? []) as Position[];
  const checklist: ChecklistInput = {
    project: proj,
    today,
    blocks: blockRows,
    plannedLines,
    aircraft: (must(aircraft as Res<Aircraft[]>) ?? []) as Aircraft[],
    people: (must(people as Res<Person[]>) ?? []) as Person[],
    crew: (must(crew as Res<ProjectCrew[]>) ?? []) as ProjectCrew[],
    positions: positionRows,
    trackers: must(trackers as Res<{ aircraft_id: string | null; deleted_at: string | null }[]>) ?? [],
    // Teslyn Cloud cannot see the laptop's folder, Oasis montaj or Diurnal QC: those checks say so
    files: null,
    geosoftInstalled: null,
    diurnalRunning: null,
    taLiveUnits: (must(units as Res<{ id: string }[]>) ?? []).length,
    cloud: {
      state: 'ok',
      stationLinks:
        must(links as Res<{ station_id: string; starts_on: string; ends_on: string | null }[]>) ?? [],
      // the office person's own phone is not the crew's: the wizard leaves that check out
      pushDevices: 0,
    },
  };
  return {
    project: proj,
    blocks: blockRows,
    positions: positionRows,
    input: {
      checklist,
      clientOrgName: p.clientOrgId ? (p.clientOrgName ?? 'Linked organisation') : null,
      clientPeople: p.clientOrgId ? (must(people2 as Res<{ user_id: string }[]>) ?? []).length : null,
      reportEmails: (must(setting as Res<ReportEmailSetting | null>) ?? null) as ReportEmailSetting | null,
      confirmed,
    },
  };
}

/** Confirm a step with its checks' counts (audited by the database). */
export async function confirmSetupStep(
  sb: SupabaseClient,
  p: Pick<PortalProject, 'id' | 'tenantId'>,
  step: string,
  checks: { readonly pass: number; readonly warn: number; readonly fail: number },
): Promise<void> {
  must(
    (await sb
      .from('project_setup_step')
      .upsert(
        { project_id: p.id, tenant_id: p.tenantId, step, checks },
        { onConflict: 'project_id,step' },
      )) as Res<null>,
  );
}

/** Undo a step's confirmation (audited). */
export async function undoSetupStep(sb: SupabaseClient, projectId: string, step: string): Promise<void> {
  must(
    (await sb.from('project_setup_step').delete().eq('project_id', projectId).eq('step', step)) as Res<null>,
  );
}

/**
 * The project's report emails (F07): daily and weekly on or off, and (decisions 883, 884) the subject and message
 * (null: Teslyn's wording), what the email carries and whether a weekly one carries the day's daily report.
 */
export async function saveReportEmails(
  sb: SupabaseClient,
  p: Pick<PortalProject, 'id' | 'tenantId'>,
  setting: ReportEmailSetting,
): Promise<void> {
  must(
    (await sb
      .from('project_report_email')
      .upsert(
        { project_id: p.id, tenant_id: p.tenantId, ...setting },
        { onConflict: 'project_id' },
      )) as Res<null>,
  );
}

/** A survey block's survey height (`block.terrain_clearance_m`, decision 822); null clears it. */
export async function saveSurveyHeight(
  sb: SupabaseClient,
  blockId: string,
  metres: number | null,
): Promise<void> {
  must((await sb.from('block').update({ terrain_clearance_m: metres }).eq('id', blockId)) as Res<null>);
}

/** The crew's hard deck and lower working height (Flight data QC settings). */
export async function saveCrewHeights(
  sb: SupabaseClient,
  projectId: string,
  heights: { readonly qc_hard_deck_m: number | null; readonly qc_lower_working_m: number | null },
): Promise<void> {
  must((await sb.from('project').update(heights).eq('id', projectId)) as Res<null>);
}

/** The base mag position's coordinates (the project's grid). */
export async function savePositionCoordinates(
  sb: SupabaseClient,
  positionId: string,
  easting: number | null,
  northing: number | null,
): Promise<void> {
  must((await sb.from('position').update({ easting, northing }).eq('id', positionId)) as Res<null>);
}

// ------------------------------------------------------------------------------------------- a client's choice (F07)

export interface ReportEmailChoice {
  readonly clientOrgId: string;
  /** True: emailed (the default when no row). */
  readonly reports: boolean;
}

/** The signed-in client's choice for one organisation (own rows only, RLS). */
export async function loadReportEmailChoice(sb: SupabaseClient, clientOrgId: string): Promise<boolean> {
  const r = must(
    (await sb
      .from('report_email_choice')
      .select('reports')
      .eq('client_org_id', clientOrgId)
      .maybeSingle()) as Res<{ reports: boolean } | null>,
  );
  return r?.reports ?? true;
}

export async function saveReportEmailChoice(
  sb: SupabaseClient,
  userId: string,
  clientOrgId: string,
  reports: boolean,
): Promise<void> {
  must(
    (await sb
      .from('report_email_choice')
      .upsert(
        { user_id: userId, client_org_id: clientOrgId, reports },
        { onConflict: 'user_id,client_org_id' },
      )) as Res<null>,
  );
}
